"use client";

import { ProcessedCourse } from "@/common/course/types";
import { Program } from "@/common/program/types";
import { ProductViewTracker } from "@/components/ProductViewTracker";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import useEnrollmentUrl from "@/lib/program/utils/programEnroll/useEnrollmentUrl";

interface ProgramProductViewTrackerProps {
  programData: Program;
  courses: ProcessedCourse[];
  skus: string[];
  path: string;
  price?: string;
  discountedPrice?: string;
  currency?: string;
  partner?: string;
  category?: string;
}

/**
 * Client component wrapper for ProductViewTracker in program detail pages.
 * Uses useEnrollmentUrl to generate the checkout URL with proper program enrollment logic.
 */
export function ProgramProductViewTracker({
  programData,
  courses,
  skus,
  path,
  price,
  discountedPrice,
  currency,
  partner,
  category,
}: ProgramProductViewTrackerProps) {
  // Fetch user enrollments and entitlements
  const { enrollments } = useClientEnrollments();
  const { entitlements } = useClientEntitlements();

  // Generate program enrollment URL with all the necessary logic
  const enrollmentUrl = useEnrollmentUrl(
    programData,
    courses,
    skus,
    enrollments,
    entitlements,
    false, // emailOptIn - false for tracking URL
  );

  return (
    <ProductViewTracker
      productId={programData.externalId || programData.objectID}
      productType="program"
      productName={programData.title}
      productUrl={`/${path}`}
      checkoutUrl={enrollmentUrl}
      metadata={{
        price: price ? parseFloat(price) : undefined,
        discountedPrice: discountedPrice
          ? parseFloat(discountedPrice)
          : undefined,
        currency: currency || "USD",
        partner,
        category,
      }}
    />
  );
}
